/**
 * Generic modal helper used by RssView and SettingsView.
 *
 * One modal at a time (re-use #zap-modal id, removes any previous one).
 * Plain DOM, no framework.  Backdrop + Escape both close.
 *
 * @param {Object}   opts
 * @param {string}   opts.title         visible title
 * @param {string}   opts.body          inner HTML for the body
 * @param {() => Promise<boolean>|boolean} opts.onSave  called when Save clicked.
 *                                                       Return false to keep modal open
 *                                                       (e.g. validation error).
 * @param {() => void} [opts.onMounted] hook after DOM is in document
 */
export function showModal({
  title,
  body,
  onSave,
  onMounted,
  onClose,
  saveLabel = 'Save',
  cancelLabel = 'Cancel',
  saveClass = 'btn--primary',
}) {
  document.getElementById('zap-modal')?.remove();

  const m = document.createElement('div');
  m.id = 'zap-modal';
  m.className = 'zap-modal';
  m.innerHTML = `
    <div class="zap-modal__backdrop"></div>
    <div class="zap-modal__panel" role="dialog" aria-modal="true" aria-label="${escAttr(title)}">
      <header class="zap-modal__h">
        <span>${escAttr(title)}</span>
        <button class="zap-modal__x" type="button" aria-label="Schließen">×</button>
      </header>
      <div class="zap-modal__body">${body}</div>
      <footer class="zap-modal__f">
        <button class="btn btn--ghost btn--sm" data-modal-cancel>${escAttr(cancelLabel)}</button>
        <button class="btn ${escAttr(saveClass)} btn--sm" data-modal-save>${escAttr(saveLabel)}</button>
      </footer>
    </div>`;
  // A native modal <dialog> (settings) lives in the top layer, above every
  // z-index: a sub-modal opened from inside it must be mounted INTO that
  // dialog, or it renders underneath and never receives pointer events.
  const host = [...document.querySelectorAll('dialog[open]')].pop() || document.body;
  host.appendChild(m);

  let closed = false;
  const close = () => {
    if (closed) return;
    closed = true;
    document.removeEventListener('keydown', onKey);
    m.remove();
    onClose?.();
  };
  const onKey = (e) => {
    if (e.key === 'Escape') close();
  };
  document.addEventListener('keydown', onKey);

  m.querySelector('.zap-modal__x').onclick = close;
  m.querySelector('.zap-modal__backdrop').onclick = close;
  m.querySelector('[data-modal-cancel]').onclick = close;
  m.querySelector('[data-modal-save]').onclick = async () => {
    const ok = await onSave();
    if (ok !== false) close();
  };

  if (typeof onMounted === 'function') onMounted();

  return { close };
}

export function confirmModal({
  title = 'Bestätigen',
  message,
  confirmLabel = 'Bestätigen',
  cancelLabel = 'Abbrechen',
  danger = false,
}) {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    showModal({
      title,
      body: `<p>${escAttr(message)}</p>`,
      saveLabel: confirmLabel,
      cancelLabel,
      saveClass: danger ? 'btn--danger' : 'btn--primary',
      onSave: () => {
        finish(true);
        return true;
      },
      onClose: () => finish(false),
    });
  });
}

export function promptModal({
  title,
  label,
  value = '',
  placeholder = '',
  saveLabel = 'Übernehmen',
}) {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (result) => {
      if (settled) return;
      settled = true;
      resolve(result);
    };
    showModal({
      title,
      body: `
        <label class="form-row">
          <span>${escAttr(label)}</span>
          <input id="zap-modal-prompt" type="text" value="${escAttr(value)}"
                 placeholder="${escAttr(placeholder)}" autocomplete="off">
        </label>`,
      saveLabel,
      cancelLabel: 'Abbrechen',
      onMounted: () => {
        const input = document.getElementById('zap-modal-prompt');
        input?.focus();
        input?.select();
      },
      onSave: () => {
        finish(document.getElementById('zap-modal-prompt')?.value ?? '');
        return true;
      },
      onClose: () => finish(null),
    });
  });
}

function escAttr(s) {
  return String(s ?? '').replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c],
  );
}
